import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, DollarSign, Search } from 'lucide-react';

import { API } from '../../utils/apiConfig';

export const AdminPayoutsTab = () => {
  const { t } = useTranslation();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [processing, setProcessing] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadPayouts = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet/admin/payouts`, { headers });
      setPayouts(data.payouts || []);
    } catch (e) {
      console.error('Load payouts failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPayouts(); }, []);

  const handleAction = async (payoutId, action) => {
    const confirmMsg = action === 'approve'
      ? t('admin.payouts.confirmApprove')
      : t('admin.payouts.confirmReject');
    if (!window.confirm(confirmMsg)) return;

    setProcessing(payoutId);
    try {
      await axios.put(`${API}/wallet/admin/payouts/${payoutId}/${action}`, {}, { headers });
      loadPayouts();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(null);
    }
  };

  const filtered = filter === 'all' ? payouts : payouts.filter(p => p.status === filter);
  const stats = {
    total: payouts.length,
    requested: payouts.filter(p => p.status === 'requested').length,
    sent: payouts.filter(p => p.status === 'sent').length,
    totalAmount: payouts.filter(p => p.status === 'sent').reduce((sum, p) => sum + p.net_amount, 0),
    totalFees: payouts.filter(p => p.status === 'sent').reduce((sum, p) => sum + p.fee, 0),
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>{t('common.loading')}</div>;

  return (
    <div data-testid="admin-payouts-tab" style={{ padding: '1rem 0' }}>
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div style={{ background: '#fef3c7', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 600 }}>{t('admin.payouts.pendingRequests')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#d97706' }}>{stats.requested}</div>
        </div>
        <div style={{ background: '#dcfce7', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: '#166534', fontWeight: 600 }}>{t('admin.payouts.totalSent')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#059669' }}>{Number(stats.totalAmount ?? 0).toFixed(2)}€</div>
        </div>
        <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: '#1d4ed8', fontWeight: 600 }}>{t('admin.payouts.feesCollected')}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1d4ed8' }}>{Number(stats.totalFees ?? 0).toFixed(2)}€</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {['all', 'requested', 'sent', 'rejected'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '0.4rem 1rem', borderRadius: '20px', border: '1px solid #d1d5db', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
            background: filter === f ? '#1d4ed8' : 'white', color: filter === f ? 'white' : '#374151'
          }}>
            {t(`admin.payouts.filter.${f}`)} {f !== 'all' && `(${payouts.filter(p => f === 'all' || p.status === f).length})`}
          </button>
        ))}
      </div>

      {/* Payouts List */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
          <DollarSign size={40} style={{ margin: '0 auto 0.5rem' }} />
          <p>{t('admin.payouts.noPending')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map(p => (
            <div key={p.id} data-testid={`payout-row-${p.id}`} style={{
              background: 'white', borderRadius: '12px', padding: '1rem', border: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem'
            }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontWeight: 600, color: '#1f2937' }}>{p.driver_name}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  {p.driver_email} {p.bank_details && `· IBAN: ${p.bank_details.slice(0, 12)}...`}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                  {p.requested_at && new Date(p.requested_at).toLocaleString()}
                </div>
              </div>

              <div style={{ textAlign: 'right', minWidth: '120px' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1f2937' }}>{Number(p.amount ?? 0).toFixed(2)}€</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  -{Number(p.fee ?? 0).toFixed(2)}€ {t('wallet.fee')} = <strong>{Number(p.net_amount ?? 0).toFixed(2)}€</strong>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {p.status === 'requested' ? (
                  <>
                    <button
                      data-testid={`approve-payout-${p.id}`}
                      onClick={() => handleAction(p.id, 'approve')}
                      disabled={processing === p.id}
                      style={{
                        padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                        background: '#059669', color: 'white', fontWeight: 600, fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', gap: '0.3rem'
                      }}
                    >
                      <CheckCircle size={16} /> {t('admin.payouts.approve')}
                    </button>
                    <button
                      data-testid={`reject-payout-${p.id}`}
                      onClick={() => handleAction(p.id, 'reject')}
                      disabled={processing === p.id}
                      style={{
                        padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #fca5a5', cursor: 'pointer',
                        background: '#fef2f2', color: '#dc2626', fontWeight: 600, fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', gap: '0.3rem'
                      }}
                    >
                      <XCircle size={16} /> {t('admin.payouts.reject')}
                    </button>
                  </>
                ) : (
                  <span style={{
                    padding: '0.35rem 0.8rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600,
                    background: p.status === 'sent' ? '#dcfce7' : '#fef2f2',
                    color: p.status === 'sent' ? '#166534' : '#991b1b'
                  }}>
                    {p.status === 'sent' ? <CheckCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> : <XCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />}
                    {t(`wallet.status.${p.status}`)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
